import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Loader2, ShieldCheck, Search, Save } from 'lucide-react';
import { toast } from 'sonner';

const ROLES = [
  { id: 'superadmin', label: 'Super Admin' },
  { id: 'admin', label: 'Amministratore' },
  { id: 'manager', label: 'Manager' },
  { id: 'staff', label: 'Dipendente' },
];

const UserRow = ({ item, onSaved, isMe }) => {
  const { API, getAuthHeaders } = useAuth();
  const [role, setRole] = useState(item.role);
  const [email, setEmail] = useState(item.email);
  const [saving, setSaving] = useState(false);

  const dirty = role !== item.role || email !== item.email;

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.patch(`${API}/admin/users/${item.id}`, { role, email }, getAuthHeaders());
      toast.success('Account aggiornato', { description: `${res.data.user.email} · ${res.data.user.role}` });
      onSaved();
    } catch (err) {
      toast.error('Modifica non riuscita', { description: err.response?.data?.detail || err.message });
      setRole(item.role);
      setEmail(item.email);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col lg:flex-row lg:items-center gap-3 p-4 border-b border-border last:border-0"
      data-testid={`user-row-${item.id}`}>
      <div className="min-w-0 lg:w-56">
        <p className="font-medium truncate">{item.name || 'Senza nome'}</p>
        <p className="text-xs text-muted-foreground">
          {item.role === 'superadmin' && <Badge variant="outline" className="mr-1 text-[10px]">Super Admin</Badge>}
          {isMe ? 'Sei tu' : `Dal ${(item.created_at || '').slice(0, 10)}`}
        </p>
      </div>
      <Input value={email} onChange={(e) => setEmail(e.target.value)} className="lg:flex-1"
        data-testid={`user-email-${item.id}`} />
      <Select value={role} onValueChange={setRole}>
        <SelectTrigger className="lg:w-48" data-testid={`user-role-${item.id}`}><SelectValue /></SelectTrigger>
        <SelectContent>
          {ROLES.map((r) => (
            <SelectItem key={r.id} value={r.id} data-testid={`role-option-${r.id}`}>{r.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button onClick={save} disabled={!dirty || saving} className="min-h-[44px] lg:w-32"
        data-testid={`save-user-${item.id}`}>
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" />Salva</>}
      </Button>
    </div>
  );
};

/** Pannello Super Admin: ruoli ed email di tutti gli account. */
const UsersAdminPage = () => {
  const { API, getAuthHeaders, user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');

  const load = async () => {
    try {
      const res = await axios.get(`${API}/admin/users`, getAuthHeaders());
      setUsers(res.data.users || []);
    } catch {
      toast.error('Elenco non disponibile');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const filtered = users.filter((u) =>
    `${u.name || ''} ${u.email || ''}`.toLowerCase().includes(query.toLowerCase()));

  return (
    <div className="space-y-6" data-testid="users-admin-page">
      <div className="flex items-center gap-3">
        <span className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
          <ShieldCheck className="w-5 h-5 text-primary" />
        </span>
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold font-outfit">Utenti</h1>
          <p className="text-muted-foreground text-sm">Chi può entrare e con quale ruolo.</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base font-outfit">Account della piattaforma</CardTitle>
          <CardDescription>
            Il Super Admin vede tutto e apre Creator Studio e Agenzia AI. Cambia ruolo o email e premi Salva.
          </CardDescription>
          <div className="relative pt-2">
            <Search className="absolute left-3 top-1/2 w-4 h-4 text-muted-foreground" />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} className="pl-9"
              placeholder="Cerca per nome o email" data-testid="users-search" />
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>
          ) : filtered.length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground" data-testid="users-empty">Nessun account trovato.</p>
          ) : (
            filtered.map((u) => (
              <UserRow key={u.id} item={u} onSaved={load} isMe={u.id === user?.id} />
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default UsersAdminPage;
